import { createFileRoute, useParams } from '@tanstack/react-router'
import { useState, useCallback, lazy, Suspense, useEffect, useMemo } from 'react'
import { usePostHog } from 'posthog-js/react'
import { Button } from '@/components/shared/atoms/Button'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSubscriptionStatus } from '@/hooks/api/useSubscriptions'
import { SubscriptionLevel, ProductDto } from '@wtf/shared'
import { useSuspenseQuery, useQueryClient } from '@tanstack/react-query'
import { collectionQueryOptions } from '@/hooks/queries/collectionQueryOptions'
import { usePageViewTracking } from '@/hooks/analytics/usePageViewTracking'
import { AnalyticsEvents } from '@/utils/analytics'
import { useCollectionProducts } from '@/hooks/api/useCollections'

// Collection Display
import LockSimpleOpenIcon from '@/icons/lock-simple-open'
import { CollectionHeaderSection } from '@/components/collections/page/CollectionHeaderSection'
// import { CollectionLooksLayout } from '@/components/collections/page/CollectionLooksLayout'
import { CollectionProductsLayout } from '@/components/collections/page/CollectionProductsLayout'

// Subscription Bottom Sheets
import { SubscriptionBottomSheet } from '@/components/subscriptions/bottom-sheets/SubscriptionBottomSheet'

// Collection Bottom Sheets
import { ReportCollectionBottomSheet } from '@/components/collections/bottom-sheets/ReportCollectionBottomSheet'
import { ArchiveConfirmationBottomSheet } from '@/components/collections/bottom-sheets/ArchiveConfirmationBottomSheet'
import { UnarchiveConfirmationBottomSheet } from '@/components/collections/bottom-sheets/UnarchiveConfirmationBottomSheet'
import { CollectionOptionsBottomSheet } from '@/components/collections/bottom-sheets/CollectionOptionsBottomSheet'

// New Product Bottom Sheets
import { CreateProductFlow } from '@/components/create/product/CreateProductFlow'
import { useBottomNav } from '@/contexts/BottomNavContext'
import { classNames } from '@/utils/classNames'
import { useRouter } from '@tanstack/react-router'
import { useAddProductToCollection } from '@/hooks/api/useCollections'

// Lazy load edit details modal for desktop
const EditCollectionDetailsModal = lazy(
    () => import('@/components/collections/modals/EditCollectionDetailsModal')
)

export const Route = createFileRoute('/collections/$id')({
    component: CollectionRoute,
    loader: ({ context: { queryClient }, params: { id } }) =>
        queryClient.ensureQueryData(collectionQueryOptions(queryClient, id)),
})

function CollectionRoute() {
    const addProductMutation = useAddProductToCollection()
    const router = useRouter()
    const { id } = useParams({ from: '/collections/$id' })
    const queryClient = useQueryClient()
    const { data: collection } = useSuspenseQuery(
        collectionQueryOptions(queryClient, id)
    )

    // Lazy load products that aren't in cache
    // For owner's collections: productIds contains ALL IDs
    // For other users' collections: productIds may only contain preview IDs (first 10)
    // We use totalProductCount to determine if more products exist beyond the preview
    const productIds = collection?.productIds || []
    const totalProducts = collection?.totalProductCount ?? 0
    const hasMoreBeyondPreview = totalProducts > productIds.length

    const firstUncachedIndex = productIds.findIndex(
        (pid) => !queryClient.getQueryData<ProductDto>(['product', pid])
    )

    // Need to fetch if: any known ID is uncached OR more products exist beyond preview
    const needsProductFetch = firstUncachedIndex !== -1 || hasMoreBeyondPreview

    // Calculate initial offset:
    // - If we have uncached IDs in our list, start from there
    // - If all known IDs are cached but more exist, start from where preview ends
    const initialOffset = firstUncachedIndex !== -1
        ? firstUncachedIndex
        : productIds.length

    // Use collection products hook to fetch missing products
    const { data: productsData, fetchNextPage, hasNextPage, isFetching: isLoadingProducts } = useCollectionProducts(
        id,
        {
            initialOffset,
            pageSize: 50,
            enabled: needsProductFetch,
        }
    )

    // Fetch all products on mount if needed
    useEffect(() => {
        if (needsProductFetch && hasNextPage && !isLoadingProducts) {
            fetchNextPage()
        }
    }, [needsProductFetch, hasNextPage, isLoadingProducts, fetchNextPage])

    // Merge initial preview productIds with any additional fetched product IDs
    // This handles the case where non-owners only get 10 preview IDs initially
    const allProductIds = useMemo(() => {
        const initialIds = collection?.productIds || []
        const fetchedIds = productsData?.pages.flatMap(page =>
            page.products.map(p => p.id)
        ) || []
        // Merge and deduplicate, preserving order
        const seen = new Set(initialIds)
        const merged = [...initialIds]
        for (const id of fetchedIds) {
            if (!seen.has(id)) {
                seen.add(id)
                merged.push(id)
            }
        }
        return merged
    }, [collection?.productIds, productsData?.pages])

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { user, loading: _userLoading } = useCurrentUser()
    const { isBottomNavVisible } = useBottomNav()
    const { withAuth } = useProtectedAction()
    const posthog = usePostHog()

    // Track page views and time on page
    usePageViewTracking({
        viewEvent: AnalyticsEvents.COLLECTION_PAGE_VIEWED,
        exitEvent: AnalyticsEvents.COLLECTION_PAGE_EXITED,
        viewProperties: {
            collection_id: collection?.id,
            collection_name: collection?.name,
            owner_id: collection?.ownerId,
            is_paid: collection?.isPaidSubscriberOnly,
            product_count: collection?.totalProductCount ?? 0,
        },
        exitProperties: {
            collection_id: collection?.id,
        },
        dependencies: [collection?.id],
    })

    // Bottom sheet state
    const [activeSheets, setActiveSheets] = useState({
        subscribe: false,
        reportCollection: false,
        optionMenu: false,
        archiveConfirmation: false,
        unarchiveConfirmation: false,
        optionAddProduct: false,
        editDetails: false,
    })

    // Get subscription status for the current user and the collection owner
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data: subscriptionStatus, isLoading: _subscriptionLoading } =
        useSubscriptionStatus(collection?.ownerId)

    // Determine if the current user is the owner of the collection
    const isOwner = !!user && !!collection && collection.ownerId === user.uid
    const isPaidCollection = collection?.isPaidSubscriberOnly || false
    const isPaidSubscriber =
        subscriptionStatus?.subscriptionType ===
        SubscriptionLevel.PAID_MONTHLY ||
        subscriptionStatus?.subscriptionType === SubscriptionLevel.PAID_ANNUAL
    // Map subscriptionStatus?.subscriptionType (string) to SubscriptionLevel enum or null
    const subscriptionType: SubscriptionLevel | null =
        subscriptionStatus?.subscriptionType &&
            Object.values(SubscriptionLevel).includes(
                subscriptionStatus.subscriptionType as SubscriptionLevel
            )
            ? (subscriptionStatus.subscriptionType as SubscriptionLevel)
            : null
    const isLocked = isPaidCollection && !isOwner && !isPaidSubscriber

    const toggleSheet = useCallback((type: string, isOpen: boolean) => {
        setActiveSheets((prev) => ({
            ...prev,
            [type]: isOpen,
        }))
    }, [])

    const handleUpgradePlan = useCallback(
        withAuth(() => {
            toggleSheet('subscribe', false)
        }),
        [withAuth, toggleSheet]
    )

    const handleThreeDotMenu = useCallback(
        withAuth(() => {
            posthog?.capture(AnalyticsEvents.COLLECTION_OPTIONS_OPENED, {
                collection_id: collection?.id,
            })
            toggleSheet('optionMenu', true)
        }),
        [withAuth, toggleSheet, posthog, collection?.id]
    )

    // Only show looks layout if there is at least 1 look
    // const lookIds = collection.lookIds || []
    // const showLooksLayout = lookIds.length > 0

    // When a new product is created, immediately add it to the real collection
    const handleNewProductCreated = useCallback(
        async (newId: string) => {
            try {
                await addProductMutation.mutateAsync({
                    collectionId: id,
                    data: { productId: newId },
                })
            } catch (error) {
                console.error('Failed to add new product to collection:', error)
            }

            // Close the add product bottom sheet
            toggleSheet('optionAddProduct', false)
        },
        [addProductMutation, id, toggleSheet]
    )

    // Memoize navigation callbacks
    const handleBack = useCallback(() => router.history.back(), [router])

    // Memoize layout handlers
    // const handleAddLook = useCallback(() => {
    //     router.navigate({
    //         to: '/looks/create',
    //         search: {
    //             collectionId: id,
    //             from: 'display',
    //         },
    //     })
    // }, [router, id])

    const handleAddProduct = useCallback(() => {
        toggleSheet('optionAddProduct', true)
    }, [toggleSheet])

    const handleEditDetails = useCallback(
        withAuth(() => {
            toggleSheet('optionMenu', false)
            toggleSheet('editDetails', true)
        }),
        [withAuth, toggleSheet]
    )

    return (
        <div className="">
            {/* Unified Header Section */}
            <CollectionHeaderSection
                collection={collection}
                isOwner={isOwner}
                onBack={handleBack}
                onMore={handleThreeDotMenu}
            />
            {/* Collection Content */}
            <section
                className={classNames(
                    'mx-auto w-full',
                    isBottomNavVisible ? 'pb-20 md:pb-0' : ''
                )}
            >
                {/* Collection Products and Looks */}
                <div
                    className={`-mt-4 md:mt-0 ${isLocked ? 'blur-sm backdrop-blur-2xl' : ''}`}
                >
                    {/* {showLooksLayout && (
                        <CollectionLooksLayout
                            lookIds={lookIds}
                            handleAddLook={handleAddLook}
                            handleRemoveLook={() => {}} // No-op in display mode
                            showAdd={isOwner}
                            editMode={false}
                        />
                    )} */}

                    <CollectionProductsLayout
                        productIds={allProductIds}
                        handleAddProduct={handleAddProduct}
                        handleRemoveProduct={() => { }} // No-op in display mode
                        showAdd={isOwner}
                        editMode={false}
                        collectionId={id}
                    />
                </div>

                {/* Desktop-only: Floating Add Product Button for Owners */}
                {isOwner && (
                    <button
                        onClick={handleAddProduct}
                        className="glass-background fixed bottom-4 left-1/2 z-30 hidden h-12 w-12 -translate-x-1/2 cursor-pointer items-center justify-center rounded-xl shadow-lg md:flex"
                        aria-label="Add product"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 5V19M5 12H19"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                )}

                {/* Unlock Collection Button */}
                {isLocked && (
                    <div
                        className={classNames(
                            'fixed inset-x-0 z-10 mx-auto w-full max-w-md px-2',
                            isBottomNavVisible
                                ? 'bottom-13'
                                : 'bottom-3.5'
                        )}
                    >
                        <Button
                            onClick={withAuth(() =>
                                toggleSheet('subscribe', true)
                            )}
                            className="flex w-full items-center justify-center gap-3 py-3.5 text-sm font-medium text-white"
                        >
                            <LockSimpleOpenIcon
                                fill="white"
                                width={20}
                                height={20}
                            />
                            Unlock collection
                        </Button>
                    </div>
                )}
            </section>

            <section className="fixed">
                {/* Subscription bottom sheet */}
                <SubscriptionBottomSheet
                    open={activeSheets.subscribe}
                    onClose={() => toggleSheet('subscribe', false)}
                    onUpgradePlan={handleUpgradePlan}
                    subscriptionType={subscriptionType}
                />

                <ArchiveConfirmationBottomSheet
                    open={activeSheets.archiveConfirmation}
                    onClose={() => toggleSheet('archiveConfirmation', false)}
                    collectionId={id}
                />

                <UnarchiveConfirmationBottomSheet
                    open={activeSheets.unarchiveConfirmation}
                    onClose={() => toggleSheet('unarchiveConfirmation', false)}
                    collectionId={id}
                />

                {/* Collection options bottom sheet */}
                <CollectionOptionsBottomSheet
                    open={activeSheets.optionMenu}
                    onClose={() => toggleSheet('optionMenu', false)}
                    onEditDetails={handleEditDetails}
                    onArchive={() => {
                        toggleSheet('optionMenu', false)
                        toggleSheet('archiveConfirmation', true)
                    }}
                    onUnarchive={() => {
                        toggleSheet('optionMenu', false)
                        toggleSheet('unarchiveConfirmation', true)
                    }}
                    onReport={() => {
                        toggleSheet('optionMenu', false)
                        toggleSheet('reportCollection', true)
                    }}
                    onShare={() => toggleSheet('optionMenu', false)}
                    isOwner={isOwner}
                    isArchived={collection.isArchived}
                />

                <ReportCollectionBottomSheet
                    open={activeSheets.reportCollection}
                    onClose={() => toggleSheet('reportCollection', false)}
                    collectionId={id}
                />

                {activeSheets.optionAddProduct && (
                    <CreateProductFlow
                        hideAddToCollection={true}
                        onClose={() => toggleSheet('optionAddProduct', false)}
                        onProductCreated={handleNewProductCreated}
                    />
                )}

                {/* Edit details modal for desktop */}
                {activeSheets.editDetails && (
                    <div className="hidden md:block">
                        <Suspense fallback={<div>Loading...</div>}>
                            <EditCollectionDetailsModal
                                open={activeSheets.editDetails}
                                onClose={() => toggleSheet('editDetails', false)}
                                collectionId={id}
                            />
                        </Suspense>
                    </div>
                )}
            </section>
        </div>
    )
}
